import React, { FC, useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import { Dispatch, iRootState } from '../../store';
import { useAsync, useDebounce, useParams } from '../../hooks';
import { IGetAllParams } from '../../services/NotificationsService';

import Content from '../../components_/Content';
import Spinner from '../../components_/Spinner';
import Pagination from '../../components_/Pagination';
import EmptyState from '../../components_/EmptyState';

import Filter from './Filter';
import List from './List';

const initialFilter: Omit<IGetAllParams, 'pageIndex'> = {
  notificationType: '',
  notificationTarget: '',
  employeesList: undefined,
  from: '',
  to: ''
};

const mapState = (state: iRootState) => ({
  data: state.notifications.data,
  list: state.notifications.list,
  reloadTick: state.alerts.notificationsReloadTick
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    getData: d.notifications.getData,
    getList: d.notifications.getList,
    reloadList: d.notifications.reloadList,
    reset: d.notifications.reset,
    setReloadSubscribed: d.alerts.setNotificationsReloadSubscribed
  };
};

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export type IProps = connectedProps;

const Notifications: FC<IProps> = ({
  data,
  list: { data: list, pagesCount },
  reloadTick,
  getData,
  getList,
  reloadList,
  reset,
  setReloadSubscribed
}) => {
  const [runGetData, isLoadingGetData, isGetDataLoadingError] = useAsync(getData, true);
  const [runGetList, isLoadingGetList, isGetListLoadingError] = useAsync(getList);
  const { pageIndex, filter, handlePageChange, handleFilterChange, handleFilterReset } = useParams(
    initialFilter,
    runGetList
  );

  // Subscribe to reload ticks while mounted so the Alerts hub owner only emits
  // (and dispatches) for this page while it is open.
  useEffect(() => {
    runGetData();
    setReloadSubscribed(true);

    return () => {
      setReloadSubscribed(false);
      reset();
    };
  }, []);

  // Auto-refresh: when a notifications-hub event bumps reloadTick, silently
  // refetch the current page (current filters/pagination). reloadList is a
  // dedicated effect (not the useAsync-wrapped getList) so the list stays
  // visible — no spinner, no blanking. useDebounce collapses a burst of events
  // into one refetch; reloadList's own staleness guard drops superseded results.
  const paramsRef = useRef<IGetAllParams | null>(null);
  paramsRef.current = { ...filter, pageIndex };

  const debouncedReloadTick = useDebounce(reloadTick, 500);
  useEffect(() => {
    if (!debouncedReloadTick) return;

    void reloadList(paramsRef.current as IGetAllParams);
  }, [debouncedReloadTick]);

  const isLoading = isLoadingGetData || isLoadingGetList;
  const isLoadingError = isGetDataLoadingError || isGetListLoadingError;

  return (
    <Content title="Уведомления">
      {!isLoadingGetData && !isGetDataLoadingError && (
        <>
          <Filter
            data={data}
            filter={filter}
            onChange={handleFilterChange}
            onReset={handleFilterReset}
            disabled={isLoadingGetList}
          />
          {!isLoadingGetList && !isGetListLoadingError && (
            <>
              {!list && <EmptyState variant="generateReport" />}
              {list && (
                <>
                  {!Boolean(list.length) && <EmptyState variant="emptyRequest" />}
                  {Boolean(list.length) && (
                    <>
                      <List list={list} />
                      <Pagination
                        pageIndex={pageIndex}
                        pagesCount={pagesCount}
                        onPageChange={handlePageChange}
                      />
                    </>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
      {isLoading && <Spinner />}
      {isLoadingError && <EmptyState variant="serverError" />}
    </Content>
  );
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(Notifications);
